import { FC } from 'react';
import { Button } from "@/components/ui/button";
import { Calculator, Trophy, Timer, Zap, ArrowLeft, Crown } from 'lucide-react';
import { useCalculatorGame } from './useCalculatorGame';
import { motion } from 'framer-motion';
import { Progress } from "@/components/ui/progress";
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import useUser from '@/hooks/useUser';

export const CalculatorGame: FC = () => {
  const navigate = useNavigate();
  const { gameState, startGame, checkAnswer, endGame, resetGame } = useCalculatorGame();
  const { profile } = useUser();

  const showConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 }
    });
  };

  const handleAnswer = (answer: number) => {
    checkAnswer(answer);
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-2 sm:p-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
        {/* Botón Volver */}
        <button
          onClick={() => navigate('/games')}
          className="mb-4 flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Volver a Juegos</span>
        </button>

        {/* Header del juego */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6 gap-2">
          <div className="flex items-center gap-2 mb-2 sm:mb-0">
            <Calculator className="w-6 h-6 text-primary animate-pulse" />
            <h2 className="text-xl sm:text-2xl font-bold">Calculadora Mágica</h2>
          </div>
          <div className="flex flex-wrap justify-center sm:justify-end items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2 min-w-[100px] justify-center">
              <Trophy className="w-5 h-5 text-yellow-500 flex-shrink-0" />
              <span className="text-base sm:text-lg font-semibold whitespace-nowrap">Puntos: {gameState.score || 0}</span>
            </div>
            {gameState.streak > 0 && (
              <div className="flex items-center gap-2 min-w-[100px] justify-center">
                <Zap className="w-5 h-5 text-orange-500 flex-shrink-0" />
                <span className="text-base sm:text-lg font-semibold whitespace-nowrap">Racha: {gameState.streak}</span>
              </div>
            )}
            <div className="flex items-center gap-2 min-w-[100px] justify-center">
              <Timer className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <span className="text-base sm:text-lg font-semibold whitespace-nowrap">Tiempo: {gameState.timeLeft}s</span>
            </div>
          </div>
        </div>

        {/* Contenido del juego */}
        {!gameState.gameStarted ? (
          <div className="text-center space-y-6 py-8">
            <h3 className="text-2xl font-bold">¿Listo para el desafío?</h3>
            <p className="text-gray-600">
              Resuelve operaciones matemáticas, gana puntos y Cerebrix.
              ¡Mientras más rápido respondas, más puntos ganarás!
            </p>
            {gameState.gameConfig && (
              <div className="text-sm text-gray-500">
                Necesitas {gameState.gameConfig.min_points_for_cerebrix} puntos para empezar a ganar Cerebrix
              </div>
            )}
            <Button
              size="lg"
              onClick={startGame}
              className="w-full max-w-md mx-auto"
            >
              ¡Comenzar!
            </Button>
          </div>
        ) : gameState.isGameOver ? (
          <div className="flex flex-col items-center justify-center space-y-4 py-6 px-4 w-full max-w-sm mx-auto">
            <h2 className="text-2xl font-bold text-center">¡Juego Terminado!</h2>
            <div className="text-4xl font-bold text-primary text-center break-words w-full">
              {gameState.score} puntos
            </div>
            {gameState.score >= 100 && (
              <div className="text-lg text-blue-500 text-center break-words w-full">
                +{Math.floor(gameState.score / 100)} Cerebrix
              </div>
            )}
            {gameState.score < 100 && (
              <p className="text-sm text-gray-600 text-center">
                Necesitas 100 puntos para ganar Cerebrix
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-4 w-full">
              <Button
                variant="outline"
                onClick={() => {
                  resetGame();
                  navigate('/games');
                }}
                className="w-full"
              >
                Volver a Juegos
              </Button>
              <Button 
                onClick={() => resetGame()}
                className="w-full"
              >
                Jugar de Nuevo
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Barra de progreso del tiempo */}
            <Progress value={(gameState.timeLeft / 60) * 100} />

            {/* Problema actual */}
            {gameState.currentProblem && (
              <div className="text-center space-y-4">
                <motion.div
                  key={gameState.currentProblem.expression}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-3xl sm:text-4xl font-bold"
                >
                  {gameState.currentProblem.expression} = ?
                </motion.div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {gameState.currentProblem.options.map((option, index) => (
                    <Button
                      key={index}
                      onClick={() => handleAnswer(option)}
                      size="lg"
                      variant="outline"
                      className="text-xl py-6"
                    >
                      {option}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
